import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { renderToStaticMarkup } from 'react-dom/server'
import { Building2, Wrench, X } from 'lucide-react'
import { getOutdoorRepairs } from '@/service/homeService'
import { buildRepairConnections } from './repairConnectionData'
import { createRepairFlightLayer } from './repairFlightLayer'
import './repairConnections.less'

function markerIcon(Icon, color, origin = false) {
    return 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(renderToStaticMarkup(
        <svg xmlns="http://www.w3.org/2000/svg" width="36" height="40" viewBox="0 0 36 40">
            <path d={origin ? 'M18 1 L11 10 H25 Z' : 'M18 39 L11 30 H25 Z'} fill={color} />
            <circle cx="18" cy={origin ? 22 : 18} r="16" fill={color} stroke="white" strokeWidth="3" />
            <Icon x="9" y={origin ? 13 : 9} width="18" height="18" color="white" strokeWidth="2" />
        </svg>
    ))
}

export default function RepairConnections({ map }) {
    const [orders, setOrders] = useState([])
    const [selectedId, setSelectedId] = useState(null)
    const requestVersion = useRef(0)
    const layers = useRef(null)
    const initialFit = useRef(false)
    const connections = useMemo(() => buildRepairConnections(orders), [orders])
    const selected = connections.find(item => item.orderId === selectedId)

    const refresh = useCallback(async () => {
        const version = ++requestVersion.current
        try {
            const result = await getOutdoorRepairs()
            if (version !== requestVersion.current) return
            if (result.retCode !== 200 || !Array.isArray(result.retData)) throw new Error('加载失败')
            setOrders(result.retData)
        } catch (_) {
            if (version === requestVersion.current) {
                // 不保留可能已经完成或转派的旧连线，避免把失败刷新显示成当前状态。
                setOrders([])
            }
        }
    }, [])

    useEffect(() => {
        refresh()
        // 全量刷新会移除已完成/转派工单；只在当前首页可见时发起后台刷新。
        const timer = setInterval(() => { if (!document.hidden) refresh() }, 60000)
        const handleChange = () => refresh()
        window.addEventListener('home-outdoor-repairs-changed', handleChange)
        return () => {
            requestVersion.current += 1
            clearInterval(timer)
            window.removeEventListener('home-outdoor-repairs-changed', handleChange)
        }
    }, [refresh])

    const fit = useCallback((items) => {
        if (!map || !items.length) return
        const bounds = new window.TMap.LatLngBounds()
        items.forEach(item => {
            bounds.extend(new window.TMap.LatLng(item.origin.lat, item.origin.lng))
            bounds.extend(new window.TMap.LatLng(item.destination.lat, item.destination.lng))
        })
        map.fitBounds(bounds, { padding: 100 })
    }, [map])

    useEffect(() => {
        if (selectedId && !connections.some(item => item.orderId === selectedId)) setSelectedId(null)
    }, [connections, selectedId])

    useEffect(() => {
        if (!map) return
        const TMap = window.TMap
        const line = createRepairFlightLayer(map, setSelectedId)
        const points = new TMap.MultiMarker({
            map, id: 'outdoor-repair-endpoints', zIndex: 4,
            styles: {
                // 机构点向下展开，同地址的维修点向上展开，两个入口都能点击。
                organ: new TMap.MarkerStyle({ src: markerIcon(Building2, '#2165d6', true), width: 36, height: 40, anchor: { x: 18, y: 0 } }),
                repair: new TMap.MarkerStyle({ src: markerIcon(Wrench, '#e96b20'), width: 36, height: 40, anchor: { x: 18, y: 40 } }),
            },
            geometries: [],
        })
        const onPointClick = event => {
            const data = event.geometry.properties
            if (data.orderId) setSelectedId(data.orderId)
            else setSelectedId(null)
        }
        points.on('click', onPointClick)
        layers.current = { line, points }
        initialFit.current = false
        return () => {
            points.off('click', onPointClick)
            line.destroy()
            points.destroy()
            layers.current = null
        }
    }, [map])

    useEffect(() => {
        if (!layers.current) return
        const { line, points } = layers.current
        const TMap = window.TMap
        const organs = new Map()
        const geometries = []
        line.setData(connections, selectedId)
        connections.forEach(item => {
            organs.set(item.organId, { id: `organ-${item.organId}`, styleId: 'organ', position: new TMap.LatLng(item.origin.lat, item.origin.lng), properties: { organId: item.organId } })
            geometries.push({ id: item.orderId, styleId: 'repair', position: new TMap.LatLng(item.destination.lat, item.destination.lng), properties: { orderId: item.orderId } })
        })
        points.setGeometries([...organs.values(), ...geometries])
        if (!initialFit.current && connections.length) {
            fit(connections)
            initialFit.current = true
        }
    }, [map, connections, selectedId, fit])

    return <>
        {selected && <section className="repair-map-detail" aria-label="外出维修工单信息">
            <button className="repair-map-icon repair-map-close" aria-label="关闭工单信息" onClick={() => setSelectedId(null)}><X size={17} /></button>
            <span className="repair-map-status">{selected.orderStatusDesc}</span>
            <h3>{selected.customerVehicleCode || selected.orderCode}</h3>
            <p className="repair-map-order">工单 {selected.orderCode}</p>
            <div className="repair-map-stop"><Building2 size={18} /><div><strong>{selected.organName}</strong><p>{selected.organAddress || '机构地址未填写'}</p></div></div>
            <div className="repair-map-stop is-destination"><Wrench size={18} /><div><strong>外出维修地点</strong><p>{selected.repairAddress || '维修地址未填写'}</p></div></div>
        </section>}
    </>
}
